<template>
  <div>
    <VDetailBoxWrapper gray tag="ul">
      <VDetailBox v-if="hasShowTechnicalInformation" tag="li">
        <template #label>
          {{ $t('substore.stock_overview_usid_label') }}
          <!-- #S111 -->
        </template>
        <template #default>
          {{ productOption.uniqueSubscriptionId }}
          <!-- #D58 -->
        </template>
      </VDetailBox>

      <VDetailBox v-if="productOption.chargedSubscriber" tag="li">
        <template #label>
          {{ $t('substore.stock_overview_charged_subscriber_label') }}
        </template>
        <template #default>
          {{ productOption.chargedSubscriber.value }}
        </template>
      </VDetailBox>

      <!-- Information - Description -->
      <VDetailBox v-for="(s, index) in productOption.subscriptionDescriptions" :key="index" :width="toVisibleText(s.text).length > 200 ? 3 : 1" tag="li">
        <template v-if="s.descriptionText && s.descriptionText.trim() !== 'raw'" #headline>
          <span v-html="s.descriptionText"></span>
        </template>
        <template #label>
          {{ s.name }}
        </template>
        <template #default>
          <span v-if="s.kind === 'html' && (!s.descriptionText || s.descriptionText.trim() !== 'raw')" v-html="format(s.text)"></span>
          <template v-else>{{ format(s.text) }}</template>
        </template>
      </VDetailBox>

      <VDetailBox v-if="texts.hintMyProducts" icon="information" :width="3" tag="li">
        <!-- #237 -->
        <span v-html="texts.hintMyProducts" />
      </VDetailBox>

      <VDetailBox v-if="productOption.reservationTerminationAllowed" icon="information" :width="3" tag="li">
        <!-- #184 -->
        {{ $t('substore.product_reserved_for_you') }}
      </VDetailBox>

      <VDetailBox v-if="canNotSucceed" icon="information" :width="3" tag="li">
        {{ $t('substore.editing_currently_not_possible') }}
      </VDetailBox>
    </VDetailBoxWrapper>

    <!-- Normal - Information Fields -->
    <div v-if="productOption.status !== 'inactive'" class="offset-left-2 offset-right-2 offset-top-1">
      <VInformationFields :fields="productOption.informationFields" />
    </div>

    <!-- #D324 -->
    <div
      v-if="productOption.inPromotionPeriod && productOption.optinPossible && texts.optinPossibleLongInfo"
      class="offset-left-2 offset-right-2 offset-top-1 offset-bottom-1"
      v-html="texts.optinPossibleLongInfo"
    />

    <VDetailBoxWrapper>
      <template v-if="!productOption.optinPossible">
        <VDetailBox v-if="isChargingInformationVisible">
          <!--
          This fieldset is only visible if AccessRight.InformationChargingDetails is granted.
          This is checked on server side and fields can only be filled if access is allowed.
          -->
          <template #headline>
            {{ $t('substore.myproducts_charging_info') }}
            <!-- #S112 -->
          </template>

          <template #default>
            <VDescriptionList>
              <VEntryButton
                role="link"
                v-for="(c, index) in productOption.chargingIntervalList"
                :key="index"
                :data="c"
                :aria-label="$t('common.details_charging_interval_link_aria_label')"
                label="substore.details"
                :hidden="!isChargingIntervalLinkVisible(c)"
                data-test="detail-link"
                @click="redirectToPaymentTransactions(c.paymentTransactionList, productOption.uniqueSubscriptionId)"
              >
                <template #name>
                  <!-- #D67 + #D68 -->
                  {{ c.intervalDurationStart }} - {{ c.intervalDurationEnd }}
                </template>
                <template #default>
                  <template v-if="isChargingIntervalVisible(c)">
                    {{ c.priceDisplay }}
                    <!-- #D69 -->
                  </template>
                </template>
              </VEntryButton>
              <VEntry name="substore.myproducts_next_charging" :data="productOption" field="nextRegularPayment" />
            </VDescriptionList>
          </template>
        </VDetailBox>

        <VDetailBox v-if="productOption.additionalInformation" :width="2 + (isChargingInformationVisible ? 0 : 1)">
          <!--
          This fieldset is only visible if AccessRight.InformationAdditionalInformation is granted.
          This is checked on server side and fields can only be filled if access is allowed.
          -->
          <template #headline>
            {{ $t('substore.stock_overview_additional_information_sub_headline') }}
            <!-- #S114 -->
          </template>
          <template #default>
            <VDescriptionListDivided>
              <VEntry
                name="substore.stock_overview_subscription_template_id_label"
                :data="productOption"
                field="additionalInformation.subscriptionTemplateId"
              />
              <VEntry name="substore.stock_overview_subscription_template_id_label" :data="productOption" field="additionalInformation.identifierType">
                {{ productOption.additionalInformation.identifierType }}:
                <!-- #D76 -->
                {{ productOption.additionalInformation.identifierValue }}
                <!-- #D77 -->
              </VEntry>
              <VEntry name="substore.stock_overview_earliest_cancellation_date_label" :data="productOption" field="cancellationDate" />
              <VEntry
                v-slot="{ value }"
                name="substore.stock_overview_preceding_product_label"
                :data="productOption"
                field="additionalInformation.previousSubscription"
              >
                {{ value.productName }}
                <!-- #D73 -->
                ({{ value.subscriptionTemplateId }}),
                <!-- #D74 -->
                {{ value.uniqueSubscriptionId }}
                <!-- #D75 -->
              </VEntry>
              <VEntry
                v-slot="{ value }"
                name="substore.stock_overview_succeeding_product_label"
                :data="productOption"
                field="additionalInformation.followupSubscription"
              >
                {{ value.productName }}
                <!-- #D87 -->
                ({{ value.subscriptionTemplateId }}),
                <!-- #D88 -->
                {{ value.uniqueSubscriptionId }}
                <!-- #D89 -->
              </VEntry>
            </VDescriptionListDivided>
          </template>
        </VDetailBox>
      </template>

      <!--
      This fieldset is only visible if AccessRight.InformationBundleInformation is granted.
      This is checked on server side and fields can only be filled if access is allowed.
      -->
      <VBundleInstance
        v-for="(bundleInstance, index) in productOption.bundleInformation"
        :key="'instance_' + index"
        :instance="bundleInstance"
        :index="index"
      />
    </VDetailBoxWrapper>

    <VButton
      :id="`${subscriptionTemplateId}_${productOption.uniqueSubscriptionId}`"
      :action.sync="grid.confirmation"
      :only-not-terminated="true"
      :options="[
        'resendDocument',
        'cancel',
        'termination',
        'reservationTermination',
        'orderDeletion',
        'reactivation',
        'resume',
        'productChange',
        'finalize',
        'administration',
      ]"
      :subscription-template-id="subscriptionTemplateId"
      :texts="texts"
      :product-option="productOption"
      class="offset-left-2 offset-right-2 offset-bottom-1 text-right"
    />

    <VButtonConfirmation
      :id="`${subscriptionTemplateId}_${productOption.uniqueSubscriptionId}`"
      :action.sync="grid.confirmation"
      :texts="texts"
      :product-option="productOption"
      :order-cancellation-abort-button-label-important="orderCancellationAbortButtonLabelImportant"
    />
  </div>
</template>

<script>
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { mapGetters } from 'vuex';
import { PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS } from '@route-types';
import VDetailBoxWrapper from '@/components/UI/DetailBox/Wrapper';
import VDetailBox from '@/components/UI/DetailBox';
import VDescriptionList from '@/components/DescriptionList';
import VDescriptionListDivided from '@/components/DescriptionList/Divided';
import VEntry from '@/components/DescriptionList/Entry';
import VEntryButton from '@/components/DescriptionList/EntryButton';
import VInformationFields from './InformationFields';

export default {
  name: 'StockProductDetails',

  components: {
    VDetailBoxWrapper,
    VDetailBox,
    VDescriptionList,
    VDescriptionListDivided,
    VEntry,
    VEntryButton,
    VInformationFields,
    VButton: () => import('@/components/Substore/StockProduct/Button'),
    VButtonConfirmation: () => import('@/components/Substore/StockProduct/ButtonConfirmation'),
    VBundleInstance: () => import('@/components/Substore/BundleInstance'),
  },

  inject: {
    grid: {
      from: 'grid',
      default: {},
    },
  },

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    productOption: {
      type: Object,
      required: true,
    },

    texts: {
      type: Object,
      required: true,
    },

    canNotSucceed: {
      type: Boolean,
      required: true,
    },

    isSingleOption: {
      type: Boolean,
      required: true,
    },

    orderCancellationAbortButtonLabelImportant: {
      type: Boolean,
      required: false,
    },
  },

  computed: {
    ...mapGetters('system', ['hasAccessPaymentTransactions', 'hasShowTechnicalInformation']),

    isChargingInformationVisible() {
      return this.productOption.chargingIntervalList?.some(this.isChargingIntervalVisible) || this.productOption.nextRegularPayment;
    },
  },

  beforeDestroy() {
    this.grid.confirmation = null;
  },

  methods: {
    isChargingIntervalVisible(c) {
      return c.intervalState === 2 || c.intervalState === 4;
    },

    isChargingIntervalLinkVisible(c) {
      return this.hasAccessPaymentTransactions && c.paymentTransactionList && this.getValidPaymentTransactionList(c.paymentTransactionList).length;
    },

    getValidPaymentTransactionList(paymentTransactionList) {
      return paymentTransactionList.filter((t) => t.terminationReason === 0);
    },

    async redirectToPaymentTransactions(paymentTransactionList, uniqueSubscriptionId) {
      await this.$store.dispatch('contract/change', { uniqueSubscriptionId });

      const validList = this.getValidPaymentTransactionList(paymentTransactionList).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      const from = validList[0].timestamp;
      const { timestamp: to, transactionId } = validList.pop();

      this.$router.push({
        name: PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS,
        params: { from, to, transactionId },
      });
    },

    toVisibleText(html) {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },

    format(text) {
      try {
        const date = parseISO(text);
        const dateFormat = text.indexOf(':') !== -1 ? 'dateTime' : 'date';
        return format(date, this.$t(`common.${dateFormat}Format`));
      } catch (e) {
        return text;
      }
    },
  },
};
</script>
